/**
 * Action: assign_request
 *
 * Set or clear a request's `triage_owner_id` — the explicit
 * "this is Sarah's request" assignment used by request queue
 * dashboards and follow-up reminders.
 *
 * Pairs with the request focus context shipped in #1784: on a
 * RequestDetail page, the user can say "assign this to Sarah" and
 * the planner fills `request_id` from `focusedRequestId`. To clear
 * the assignment, pass `assignee_person_id: null` (or have the user
 * say "unassign this request").
 *
 * Why triage_owner_id vs claimed_by_id?
 * -------------------------------------
 * The requests table has three assignee-shaped columns:
 *   - triage_owner_id (FK → people) — formal assignment, the one
 *     that drives queue dashboards + follow-up reminder targeting.
 *     This is what we mutate.
 *   - follow_up_owner_id (FK → auth.users) — secondary, for the
 *     follow-up workflow specifically.
 *   - claimed_by_id (FK → auth.users) — claim-on-pickup with
 *     race-safe winner-takes-all semantics (set by useClaimRequest).
 *     Not appropriate for an "assign to X" verb because the
 *     contract is "I'm taking this", not "you're taking this".
 *
 * Inverse
 * -------
 * Symmetric. We capture the CURRENT assignee at execute time so the
 * inverse rolls back to whoever was there before — even if it was
 * null (unassigned). One click of "Undo: restore assignment" returns
 * to that prior state.
 *
 * Out of scope (sibling iterations)
 * ---------------------------------
 * - "Assign me" requires the planner to know the caller's
 *   `people.id`. The current system prompt doesn't surface that;
 *   the planner falls back to asking a clarifying question, which
 *   is the correct safety behavior. Future enhancement: include
 *   the caller's own person_id in the prompt so "assign me" works
 *   without a clarifying round-trip.
 * - Recipient notification on assignment change (the inverse end —
 *   tell the newly-assigned person they've been assigned). Same
 *   reasoning as update_request_status: noisy for Solomon-initiated
 *   changes. Add a notify boolean when desired.
 */

import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { logAudit } from "@/lib/audit";
import { registerAction } from "../registry";
import type { ActionDefinition, ActionResult } from "../types";

const assignRequestParams = z.object({
  request_id: z.string().guid(),
  /**
   * `people.id` of the assignee. Pass `null` (or omit) to unassign.
   * The planner is told this is a people.id specifically — NOT an
   * auth.users.id — because `triage_owner_id` references the
   * people table.
   */
  assignee_person_id: z.string().guid().nullable().optional(),
  /**
   * Optional freeform reason. Stored on the audit row; not written
   * to the request itself.
   */
  note: z.string().trim().max(2000).optional().nullable(),
});

type AssignRequestParams = z.infer<typeof assignRequestParams>;

interface ResolvedRequest {
  id: string;
  title: string;
  currentAssigneeId: string | null;
  currentAssigneeName: string | null;
}

async function resolveRequest(
  organizationId: string,
  requestId: string,
): Promise<ResolvedRequest> {
  // Pull the request + current assignee's display name in one round
  // trip. The joined `current_assignee` shape is supabase-js's
  // standard pattern for FK-to-people lookups elsewhere in the
  // codebase.
  const { data, error } = await supabase
    .from("requests")
    .select(
      "id, summary, triage_owner_id, current_assignee:people!requests_triage_owner_id_fkey(first_name, last_name)",
    )
    .eq("organization_id", organizationId)
    .eq("id", requestId)
    .maybeSingle();

  if (error || !data) {
    throw new Error("Request not found in this organization.");
  }

  // supabase-js types embed-arrays as `Array | object | null`; we
  // narrow at runtime. If the FK didn't resolve (e.g. the assignee
  // was deleted), we treat the assignment as effectively cleared.
  const joinedRaw = (data as { current_assignee?: unknown }).current_assignee;
  const joined = Array.isArray(joinedRaw)
    ? joinedRaw[0]
    : (joinedRaw as { first_name?: string | null; last_name?: string | null } | null);
  const currentAssigneeName = joined
    ? [joined.first_name ?? "", joined.last_name ?? ""]
        .map((s) => s.trim())
        .filter(Boolean)
        .join(" ") || null
    : null;

  return {
    id: data.id,
    title: (data as { summary: string | null }).summary ?? "this request",
    currentAssigneeId: (data as { triage_owner_id: string | null })
      .triage_owner_id,
    currentAssigneeName,
  };
}

/**
 * Look up the display name of a candidate assignee. Returns the
 * formatted name or `null` if the person isn't in the org. Used by
 * `buildPreview` so the proposed-action card surfaces a real name
 * instead of a UUID.
 */
async function resolvePersonName(
  organizationId: string,
  personId: string,
): Promise<string | null> {
  const { data, error } = await supabase
    .from("people")
    .select("first_name, last_name")
    .eq("organization_id", organizationId)
    .eq("id", personId)
    .maybeSingle();
  if (error || !data) return null;
  const name = [data.first_name ?? "", data.last_name ?? ""]
    .map((s) => s.trim())
    .filter(Boolean)
    .join(" ");
  return name || null;
}

const assignRequestDefinition: ActionDefinition<AssignRequestParams> = {
  id: "assign_request",
  label: "Assign request",
  description:
    "Set or clear a request's assigned owner (triage_owner_id). Pass assignee_person_id=null to unassign.",
  requiredTier: "staff",
  paramsSchema: assignRequestParams,
  // Assignment ripples to queue dashboards, follow-up reminders, and
  // the assignee's notification feed. Confirm by default so the user
  // sees the diff before committing.
  defaultRequiresConfirm: true,
  async buildPreview(params, ctx) {
    const request = await resolveRequest(ctx.organizationId, params.request_id);
    const nextAssigneeId = params.assignee_person_id ?? null;

    if ((request.currentAssigneeId ?? null) === nextAssigneeId) {
      // No-op transition. Same idea as update_request_status: surface
      // a precondition_failed via thrown Error so the UI renders a
      // benign "already assigned to X" card.
      const who =
        request.currentAssigneeName ??
        (request.currentAssigneeId ? "someone" : "no one");
      throw new Error(
        `Request "${request.title}" is already assigned to ${who}.`,
      );
    }

    // If assigning to a specific person, surface their name in the
    // preview. Falling through to an "unknown" label here is honest:
    // the planner may have been given an id by the user that doesn't
    // exist in the org — better to ask than guess.
    let nextLabel = "no one (unassign)";
    if (nextAssigneeId) {
      const name = await resolvePersonName(ctx.organizationId, nextAssigneeId);
      if (!name) {
        throw new Error(
          "Proposed assignee was not found in this organization.",
        );
      }
      nextLabel = name;
    }

    const currentLabel = request.currentAssigneeName
      ? request.currentAssigneeName
      : "unassigned";
    return {
      summary: `Assign "${request.title}" to ${nextLabel} (currently ${currentLabel})`,
    };
  },
  async execute(params, ctx): Promise<ActionResult> {
    // Re-resolve at execute time so the inverse captures the
    // CURRENT assignee, not whatever buildPreview saw. Same
    // race-window correctness pattern as update_request_status.
    let request: ResolvedRequest;
    try {
      request = await resolveRequest(ctx.organizationId, params.request_id);
    } catch (err) {
      return {
        ok: false,
        summary: "Could not load request",
        error: err instanceof Error ? err.message : String(err),
      };
    }

    const nextAssigneeId = params.assignee_person_id ?? null;

    if ((request.currentAssigneeId ?? null) === nextAssigneeId) {
      // Idempotent — no UPDATE, no audit. Return ok with no inverse
      // so the toast closes cleanly.
      return {
        ok: true,
        summary: nextAssigneeId
          ? `"${request.title}" is already assigned to ${request.currentAssigneeName ?? "that person"}`
          : `"${request.title}" is already unassigned`,
        inverse: null,
      };
    }

    // Resolve the new assignee's name for the success toast — also
    // doubles as an existence check.
    let newAssigneeName: string | null = null;
    if (nextAssigneeId) {
      newAssigneeName = await resolvePersonName(
        ctx.organizationId,
        nextAssigneeId,
      );
      if (!newAssigneeName) {
        return {
          ok: false,
          summary: "Proposed assignee was not found in this organization.",
          error: "assignee_not_found",
        };
      }
    }

    const { error } = await supabase
      .from("requests")
      .update({ triage_owner_id: nextAssigneeId })
      .eq("organization_id", ctx.organizationId)
      .eq("id", params.request_id);

    if (error) {
      return {
        ok: false,
        summary: `Failed to assign "${request.title}"`,
        error: error.message,
      };
    }

    const audit = await logAudit({
      action: "request.assign",
      tableName: "requests",
      recordId: params.request_id,
      organizationId: ctx.organizationId,
      userId: ctx.userId,
      oldData: {
        triage_owner_id: request.currentAssigneeId,
        triage_owner_name: request.currentAssigneeName,
      },
      newData: {
        triage_owner_id: nextAssigneeId,
        triage_owner_name: newAssigneeName,
        previous_triage_owner_id: request.currentAssigneeId,
        title: request.title,
        note: params.note ?? null,
        source: ctx.source,
        reason: ctx.reason,
      },
    });

    const summary = nextAssigneeId
      ? `Assigned "${request.title}" to ${newAssigneeName}`
      : `Unassigned "${request.title}"`;

    // Inverse rolls back to the captured prior state. If there was
    // no prior assignee, the inverse assigns to null again (which
    // is a no-op on a second click — and the executor correctly
    // detects that as idempotent).
    return {
      ok: true,
      summary,
      auditLogId: audit?.id ?? null,
      inverse: {
        actionId: "assign_request",
        params: {
          request_id: params.request_id,
          assignee_person_id: request.currentAssigneeId,
        },
        label: request.currentAssigneeName
          ? `Undo: reassign to ${request.currentAssigneeName}`
          : "Undo: unassign",
      },
    };
  },
};

registerAction(assignRequestDefinition);

export { assignRequestDefinition };
